const KL_STONES_ROOT_SELECTOR = '[data-kl-stones-symbols]';
const KL_STONES_LIST_SELECTOR = '[data-kl-stones-symbols-list]';
const KL_STONES_ITEM_SELECTOR = '[data-kl-stones-symbols-item]';
const KL_STONES_NAME_SELECTOR = '[data-kl-stones-symbols-name]';
const KL_STONES_DESCRIPTION_SELECTOR = '[data-kl-stones-symbols-description]';
const KL_STONES_IMAGE_SELECTOR = '[data-kl-stones-symbols-image]';

const KL_STONES_CACHE_PREFIX = 'kl-stones-symbols';
const KL_STONES_CACHE_SEPARATOR = '|';
const KL_STONES_API_ORIGIN = 'https://sheets.googleapis.com/v4/spreadsheets';
const KL_STONES_API_QUERY = 'majorDimension=ROWS&valueRenderOption=FORMATTED_VALUE&dateTimeRenderOption=FORMATTED_STRING';
const KL_STONES_DEFAULT_TIMEOUT_MS = 8000;
const KL_STONES_MIN_TIMEOUT_MS = 1000;
const KL_STONES_MAX_TIMEOUT_MS = 30000;
const KL_STONES_MAX_ITEMS = 24;
const KL_STONES_ALLOWED_PROTOCOLS = ['http:', 'https:'];
const KL_STONES_PART_SEPARATOR = '|';

const KL_STONES_SOURCES = [
  { key: 'one', datasetPrefix: 'klStonesSymbolsSourceOne' },
  { key: 'two', datasetPrefix: 'klStonesSymbolsSourceTwo' },
];

const KL_STONES_REQUESTS = new Map();

function normalizeKey(value) {
  if (typeof value !== 'string') return '';

  return value
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}


function getCellText(row, index) {
  const cell = Array.isArray(row) ? row[index] : undefined;

  if (cell === undefined || cell === null) return '';

  return String(cell).trim();
}

function getDescriptionText(value) {
  return String(value)
    .split(KL_STONES_PART_SEPARATOR)
    .map((part) => part.trim())
    .filter((part) => part !== '')
    .join(', ');
}

function getSheetRange(range) {
  const trimmedRange = typeof range === 'string' ? range.trim() : '';

  if (!trimmedRange) return '';
  if (trimmedRange.includes('!') || trimmedRange.startsWith("'")) return trimmedRange;

  return `'${trimmedRange.split("'").join("''")}'`;
}

function buildSheetRequestUrl(source) {
  const range = encodeURIComponent(getSheetRange(source.range));
  const baseUrl = `${KL_STONES_API_ORIGIN}/${encodeURIComponent(source.spreadsheetId)}/values/${range}`;

  return `${baseUrl}?key=${encodeURIComponent(source.apiKey)}&${KL_STONES_API_QUERY}`;
}

function getColumnIndexes(headerRow, columns) {
  const positional = { name: 0, description: 1, image: 2 };

  if (!headerRow) return positional;

  const headerKeys = headerRow.map((cell, index) => normalizeKey(getCellText(headerRow, index)));
  const resolve = (configuredName, fallbackIndex) => {
    const configuredKey = normalizeKey(configuredName);
    const foundIndex = configuredKey ? headerKeys.indexOf(configuredKey) : -1;

    return foundIndex === -1 ? fallbackIndex : foundIndex;
  };

  return {
    name: resolve(columns.name, positional.name),
    description: resolve(columns.description, positional.description),
    image: resolve(columns.image, positional.image),
  };
}

function getDriveImageUrl(url) {
  if (!url.hostname.endsWith('drive.google.com')) return null;

  const pathMatch = url.pathname.match(/\/file\/d\/([^/?#]+)/);
  const fileId = pathMatch ? pathMatch[1] : url.searchParams.get('id');

  return fileId ? `https://drive.google.com/thumbnail?id=${encodeURIComponent(fileId)}&sz=w200` : null;
}

function getSafeImageUrl(rawUrl) {
  const trimmedUrl = typeof rawUrl === 'string' ? rawUrl.trim() : '';

  if (!trimmedUrl) return null;

  try {
    const url = new URL(trimmedUrl, document.baseURI);

    if (!KL_STONES_ALLOWED_PROTOCOLS.includes(url.protocol)) return null;

    return getDriveImageUrl(url) || url.href;
  } catch (error) {
    console.debug('kl-stones-symbols: an image cell is not a usable URL', { rawUrl, error });
    return null;
  }
}

function buildEntries(rows, hasHeaderRow, columns) {
  if (!Array.isArray(rows) || rows.length === 0) return [];

  const indexes = getColumnIndexes(hasHeaderRow ? rows[0] : null, columns);
  const dataRows = hasHeaderRow ? rows.slice(1) : rows;
  const entries = [];
  const seenKeys = new Set();

  dataRows.forEach((row) => {
    const name = getCellText(row, indexes.name);
    const key = normalizeKey(name);

    if (!key) return;

    if (seenKeys.has(key)) {
      console.debug('kl-stones-symbols: the tab lists this name more than once', { name });
      return;
    }

    seenKeys.add(key);
    entries.push({
      key,
      name,
      description: getDescriptionText(getCellText(row, indexes.description)),
      imageUrl: getSafeImageUrl(getCellText(row, indexes.image)),
    });
  });

  return entries;
}

function getTagValue(tag, prefix) {
  const safeTag = typeof tag === 'string' ? tag.trim() : '';
  const safePrefix = typeof prefix === 'string' ? prefix.trim() : '';

  if (!safeTag || !safePrefix) return '';
  if (!safeTag.toLowerCase().startsWith(safePrefix.toLowerCase())) return '';

  return safeTag.slice(safePrefix.length).trim();
}

function getBoundedNumber(rawValue, fallback, minimum, maximum) {
  const parsedValue = Number.parseInt(rawValue, 10);

  if (!Number.isFinite(parsedValue)) return fallback;

  return Math.min(Math.max(parsedValue, minimum), maximum);
}

function getCacheKey(source, options) {
  return [
    KL_STONES_CACHE_PREFIX,
    options.cacheVersion,
    String(options.hasHeaderRow),
    source.columns.name,
    source.columns.description,
    source.columns.image,
    source.spreadsheetId,
    source.range,
  ].join(KL_STONES_CACHE_SEPARATOR);
}

function readCachedEntries(cacheKey, cacheMinutes) {
  try {
    const rawValue = window.sessionStorage.getItem(cacheKey);

    if (!rawValue) return null;

    const cached = JSON.parse(rawValue);

    if (!Array.isArray(cached?.entries)) return null;

    const isExpired = cacheMinutes > 0 && Date.now() - cached.savedAt > cacheMinutes * 60000;

    if (isExpired) {
      window.sessionStorage.removeItem(cacheKey);
      return null;
    }

    return cached.entries;
  } catch (error) {
    console.debug('kl-stones-symbols: the session cache could not be read', { cacheKey, error });
    return null;
  }
}

function writeCachedEntries(cacheKey, entries) {
  try {
    window.sessionStorage.setItem(cacheKey, JSON.stringify({ savedAt: Date.now(), entries }));
  } catch (error) {
    console.debug('kl-stones-symbols: the session cache could not be written', { cacheKey, error });
  }
}

async function fetchSheetRows(source, timeoutMs) {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

  try {
    const response = await fetch(buildSheetRequestUrl(source), {
      signal: controller.signal,
      credentials: 'omit',
      headers: { Accept: 'application/json' },
    });

    const data = await response.json();

    if (!response.ok || data?.error) {
      throw new Error(data?.error?.message || `The sheet request failed (${response.status})`);
    }

    return Array.isArray(data?.values) ? data.values : [];
  } finally {
    clearTimeout(timeoutId);
  }
}

function loadSourceEntries(source, options) {
  const cacheKey = getCacheKey(source, options);
  const cachedEntries = readCachedEntries(cacheKey, options.cacheMinutes);

  if (cachedEntries) return Promise.resolve(cachedEntries);

  const pendingRequest = KL_STONES_REQUESTS.get(cacheKey);

  if (pendingRequest) return pendingRequest;

  const request = fetchSheetRows(source, options.timeoutMs)
    .then((rows) => {
      const entries = buildEntries(rows, options.hasHeaderRow, source.columns);

      if (entries.length === 0) console.debug('kl-stones-symbols: the tab has no usable rows', { range: source.range });

      writeCachedEntries(cacheKey, entries);
      return entries;
    })
    .catch((error) => {
      console.debug('kl-stones-symbols: the tab could not be loaded', { range: source.range, error });
      return [];
    })
    .finally(() => {
      KL_STONES_REQUESTS.delete(cacheKey);
    });

  KL_STONES_REQUESTS.set(cacheKey, request);

  return request;
}

function getBlockOptions(dataset) {
  return {
    cacheVersion: dataset.klStonesSymbolsCacheVersion || '1',
    cacheMinutes: getBoundedNumber(dataset.klStonesSymbolsCacheMinutes, 0, 0, 1440),
    timeoutMs: getBoundedNumber(
      dataset.klStonesSymbolsTimeout,
      KL_STONES_DEFAULT_TIMEOUT_MS,
      KL_STONES_MIN_TIMEOUT_MS,
      KL_STONES_MAX_TIMEOUT_MS
    ),
    hasHeaderRow: dataset.klStonesSymbolsHeaderRow !== 'false',
    separator: dataset.klStonesSymbolsSeparator || '',
    isUnmatchedShown: dataset.klStonesSymbolsShowUnmatched === 'true',
    maxItems: getBoundedNumber(dataset.klStonesSymbolsMaxItems, KL_STONES_MAX_ITEMS, 1, KL_STONES_MAX_ITEMS),
  };
}

function getBlockSources(dataset) {
  const sources = new Map();
  const apiKey = (dataset.klStonesSymbolsApiKey || '').trim();
  const spreadsheetId = (dataset.klStonesSymbolsSpreadsheetId || '').trim();

  if (!apiKey || !spreadsheetId) return sources;

  KL_STONES_SOURCES.forEach(({ key, datasetPrefix }) => {
    const range = (dataset[`${datasetPrefix}Range`] || '').trim();
    const prefix = (dataset[`${datasetPrefix}Prefix`] || '').trim();

    if (!range || !prefix) return;

    sources.set(key, {
      apiKey,
      spreadsheetId,
      range,
      prefix,
      columns: {
        name: (dataset[`${datasetPrefix}NameColumn`] || '').trim(),
        description: (dataset[`${datasetPrefix}DescriptionColumn`] || '').trim(),
        image: (dataset[`${datasetPrefix}ImageColumn`] || '').trim(),
      },
    });
  });

  return sources;
}

function renderItemImage(item, imageUrl) {
  const image = item.querySelector(KL_STONES_IMAGE_SELECTOR);

  if (!image) return;

  if (!imageUrl) {
    image.remove();
    return;
  }

  image.addEventListener(
    'error',
    () => {
      console.debug('kl-stones-symbols: an image could not be loaded', { imageUrl });
      image.remove();
    },
    { once: true }
  );

  image.src = imageUrl;
  image.hidden = false;
}

function renderItem(item, entry, separator) {
  const name = item.querySelector(KL_STONES_NAME_SELECTOR);
  const description = item.querySelector(KL_STONES_DESCRIPTION_SELECTOR);

  if (name) name.textContent = entry.description ? `${entry.name} ${separator}` : entry.name;
  if (description) description.textContent = entry.description;

  renderItemImage(item, entry.imageUrl);

  item.hidden = false;
}

async function renderStonesSymbols(root) {
  const list = root.querySelector(KL_STONES_LIST_SELECTOR);

  if (!list) return;

  const items = Array.from(list.querySelectorAll(KL_STONES_ITEM_SELECTOR));

  if (items.length === 0) return;

  const options = getBlockOptions(root.dataset);
  const sources = getBlockSources(root.dataset);

  if (sources.size === 0) {
    console.debug('kl-stones-symbols: the block needs an API key, a spreadsheet ID and at least one tab');
    return;
  }

  const sourceKeys = Array.from(sources.keys());
  const entryLists = await Promise.all(sourceKeys.map((sourceKey) => loadSourceEntries(sources.get(sourceKey), options)));

  const indexBySource = new Map(
    sourceKeys.map((sourceKey, position) => [sourceKey, new Map(entryLists[position].map((entry) => [entry.key, entry]))])
  );

  let renderedCount = 0;

  items.forEach((item) => {
    const sourceKey = item.dataset.klStonesSymbolsSource;
    const source = sources.get(sourceKey);
    const tagValue = source ? getTagValue(item.dataset.klStonesSymbolsTag, source.prefix) : '';
    const entry = tagValue ? indexBySource.get(sourceKey)?.get(normalizeKey(tagValue)) : null;

    if (!entry && !(tagValue && options.isUnmatchedShown)) {
      if (tagValue) console.debug('kl-stones-symbols: the tab has no row for this tag', { tagValue, sourceKey });
      item.remove();
      return;
    }

    if (renderedCount >= options.maxItems) {
      item.remove();
      return;
    }

    renderItem(item, entry || { name: tagValue, description: '', imageUrl: null }, options.separator);
    renderedCount += 1;
  });
  
  if (renderedCount > 0) root.hidden = false;
}

function initStonesSymbols(root) {
  if (!root || root.dataset.klStonesSymbolsReady === 'true') return;

  root.dataset.klStonesSymbolsReady = 'true';

  renderStonesSymbols(root).catch((error) => {
    console.debug('kl-stones-symbols: the block could not be rendered', { error });
  });
}

document.querySelectorAll(KL_STONES_ROOT_SELECTOR).forEach(initStonesSymbols);

document.addEventListener('shopify:section:load', (event) => {
  event.target.querySelectorAll(KL_STONES_ROOT_SELECTOR).forEach(initStonesSymbols);
});
